import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "./styles/_index.scss";
import store from "../../store";
import { useTheme, useMediaQuery } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DownloadIcon from "@mui/icons-material/Download";

import { useSelector } from "react-redux";
import useWebSocket from "react-use-websocket";
import axios from "axios";

import BasicModal from "../../components/modal";
import Logo from "../../assets/logoMain.png";

interface ChatMessage {
  id: number;
  file?: string;
  file_name?: string;
  user_type: string;
  date_time: string;
  message: string;
  user?: string;
}

interface Rooms {
  user_id: number;
  user_name: string;
  operation: string;
  chat_id: string;
  unread_admin: string;
  is_sso?: boolean;
  forecast_id?: string;
  project_location?: string;
}

interface GroupedUser {
  user_id: number;
  user_name: string;
  rooms: Rooms[];
  totalUnread: number;
  is_sso?: boolean;
  groupKey: string;
}

interface ImagePreviewModalProps {
  imageSrc: string;
  onClose: () => void;
}

const AVATAR_COLORS = ["tg-avatar--green", "tg-avatar--teal", "tg-avatar--purple", "tg-avatar--orange", "tg-avatar--green"];

const getAvatarClass = (name: string, size?: "sm"): string => {
  const code = name?.charCodeAt(0) || 0;
  const colorClass = AVATAR_COLORS[code % AVATAR_COLORS.length];
  return `tg-avatar ${colorClass}${size ? " tg-avatar--sm" : ""}`;
};

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ imageSrc, onClose }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const zoomIn = () => setScale((s) => Math.min(s + 0.25, 4));
  const zoomOut = () => setScale((s) => Math.max(s - 0.25, 0.5));
  const reset = () => { setScale(1); setPosition({ x: 0, y: 0 }); };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = imageSrc;
    a.download = "chat-image";
    a.click();
  };

  const onMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - startPos.x, y: e.clientY - startPos.y });
  };

  const toolbarBtns = [
    { icon: <ZoomOutIcon fontSize="small" />, action: zoomOut, title: "Zoom out" },
    { icon: <ZoomInIcon fontSize="small" />, action: zoomIn, title: "Zoom in" },
    { icon: <RestartAltIcon fontSize="small" />, action: reset, title: "Reset" },
    { icon: <DownloadIcon fontSize="small" />, action: handleDownload, title: "Download" },
  ];

  return (
    <div className="tg-modal-overlay" onClick={onClose}>
      <div className="tg-modal-toolbar" onClick={(e) => e.stopPropagation()}>
        {toolbarBtns.map(({ icon, action, title }) => (
          <button key={title} onClick={action} title={title} className="tg-modal-btn">
            {icon}
          </button>
        ))}
        <div className="tg-modal-sep" />
        <span className="tg-modal-scale">{Math.round(scale * 100)}%</span>
        <div className="tg-modal-sep" />
        <button onClick={onClose} className="tg-modal-btn tg-modal-btn--close">
          <CloseIcon fontSize="small" />
        </button>
      </div>
      <div
        className="tg-modal-imgwrap"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={() => setDragging(false)}
        onMouseLeave={() => setDragging(false)}
        style={{
          cursor: dragging ? "grabbing" : scale > 1 ? "grab" : "default",
          transform: `translate(${position.x}px, ${position.y}px)`,
        }}
      >
        <img
          src={imageSrc}
          alt="Preview"
          draggable={false}
          style={{
            transform: `scale(${scale})`,
            transition: dragging ? "none" : "transform 0.2s ease",
          }}
          className="tg-modal-img"
        />
      </div>
      <p className="tg-modal-hint">Click outside or press Esc to close</p>
    </div>
  );
};

const formatMessageTime = (dateTimeString: string): string => {
  let dateStr = dateTimeString.replace(" ", "T");
  if (!dateStr.includes("Z") && !dateStr.includes("+")) {
    dateStr = dateStr.split(".")[0] + "Z";
  }
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? "" : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const deduplicateRooms = (rooms: Rooms[]): Rooms[] => {
  const seen = new Set<string>();
  return rooms.filter((r) => {
    if (seen.has(r.chat_id)) return false;
    seen.add(r.chat_id);
    return true;
  });
};

const normalizeRoom = (room: any): Rooms => {
  const userId = room.user_id;
  const chatId = room.chat_id || "";
  const isSso = room.is_sso === true || userId === 0 || userId === "0";

  let projectLocation = room.project_location || null;
  if (!isSso && !projectLocation && chatId.includes("_")) {
    const parts = chatId.split("_");
    if (parts[0] === String(userId)) {
      try {
        projectLocation = decodeURIComponent(parts.slice(1).join("_"));
      } catch {
        projectLocation = parts.slice(1).join("_");
      }
    }
  }

  return {
    ...room,
    user_id: userId,
    is_sso: isSso,
    forecast_id: isSso ? chatId : (room.forecast_id || null),
    project_location: projectLocation,
  };
};

const ChatAdmin: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState("");
  const [activeChat, setActiveChat] = useState<string>("");
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [isSSO, setIsSSO] = useState(false);
  const [userData, setUserData] = useState({ name: "", operation: "" });
  const [rooms, setRooms] = useState<Rooms[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showFileModal, setShowFileModal] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [searchUsers, setSearchUsers] = useState("");
  const [showSidebar, setShowSidebar] = useState(true);

  const activeChatRef = useRef<string>("");
  const currentUserIdRef = useRef<string>("");
  const isSSORef = useRef<boolean>(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fileInputRef = useRef<HTMLInputElement>(null);
  const msgRef = useRef<HTMLDivElement>(null);

  const reduxData = useSelector((state: any) => state?.app);
  const socketUrl = `wss://${process.env.REACT_APP_WEBSOCKET_IP}/ws/chat/${localStorage.getItem("user_id")}/`;
  
  const { sendJsonMessage } = useWebSocket(socketUrl, {
    onOpen: () => {
      sendJsonMessage({ mode: "listchats", token: localStorage.getItem("token"), user_type: "admin" });
    },
    shouldReconnect: () => true,
    onMessage: (event) => processWebSocketMessage(event),
  });

  useEffect(() => {
    const interval = setInterval(() => {
      sendJsonMessage({ mode: "listchats", token: localStorage.getItem("token"), user_type: "admin" });
    }, 30000);
    return () => clearInterval(interval);
  }, [sendJsonMessage]);

  useEffect(() => {
    msgRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (reduxData?.toggle !== undefined) {
      setShowSidebar(reduxData.toggle);
    }
  }, [reduxData?.toggle]);

  const groupedUsers = useMemo(() => {
    const userMap = new Map<string, GroupedUser>();

    rooms.forEach((room) => {
      if (room.user_id.toString() === localStorage.getItem("user_id")) return;

      const groupKey = room.is_sso
        ? `sso_${room.user_name || room.chat_id}`
        : `user_${room.user_id}`;

      const userName = room.user_name || (room.is_sso ? `SSO User ${room.forecast_id}` : "Unknown");

      if (!userMap.has(groupKey)) {
        userMap.set(groupKey, {
          user_id: room.user_id,
          user_name: userName,
          rooms: [],
          totalUnread: 0,
          is_sso: room.is_sso,
          groupKey
        });
      }

      const group = userMap.get(groupKey)!;
      group.rooms.push(room);
      group.totalUnread += parseInt(room.unread_admin || "0");
    });

    const result = Array.from(userMap.values()).map((group) => {
      if (!group.is_sso) {
        const hasProjectRooms = group.rooms.some((r) => r.project_location);
        if (hasProjectRooms) {
          group.rooms = group.rooms.filter((r) => r.project_location);
          group.totalUnread = group.rooms.reduce((sum, r) => sum + parseInt(r.unread_admin || "0"), 0);
        }
      }
      return group;
    });

    return result;
  }, [rooms]);

  const processWebSocketMessage = useCallback((event: MessageEvent) => {
    const data = JSON.parse(event.data);
    const currentActive = activeChatRef.current;
    const currentUser = currentUserIdRef.current;
    const currentIsSSO = isSSORef.current;

    switch (data.mode) {
      case "listchats":
        const rawRooms: Rooms[] = (data.rooms || []).map(normalizeRoom);
        const deduped = deduplicateRooms(rawRooms);
        setRooms(deduped);

        const totalUnread = deduped.reduce((sum, r) => sum + parseInt(r.unread_admin || "0"), 0);
        store.dispatch({ type: "UNREAD_USER", payload: totalUnread });

        const activeRoom = deduped.find(r => r.chat_id === currentActive);
        if (activeRoom?.user_name) {
          setUserData(prev => ({ ...prev, name: activeRoom.user_name }));
        }
        break;

      case "createchat":
      case "latest":
        if (!data.userdata) break;

        const receivedChatId = data.rooms?.chat_id || data.rooms?.forecast_id;
        const receivedUserId = data.userdata?.id?.toString() || data.userdata?.forecast_id?.toString();
        const isForCurrentChat = receivedChatId === currentActive || receivedUserId === currentUser;

        if (!isForCurrentChat) {
          sendJsonMessage({ mode: "listchats", token: localStorage.getItem("token"), user_type: "admin" });
          break;
        }

        setMessages(data.chats || []);
        setUserData({
          name: data.userdata?.name || "",
          operation: data.userdata?.operation || "",
        });

        const roomId = data.rooms?.chat_id || data.rooms?.forecast_id;
        if (roomId) {
          sendJsonMessage({ mode: "readmessages", chat_id: roomId, user_type: "admin" });
        }
        break;

      case "sendmsg":
        sendJsonMessage({ mode: "listchats", token: localStorage.getItem("token"), user_type: "admin" });

        if (data.chats?.length > 0 && data.chat_id === currentActive) {
          setMessages(data.chats);
        } else if (currentUser) {
          sendJsonMessage({
            mode: "latest",
            user_id: currentUser,
            chat_id: currentActive,
            user_type: "admin",
            send_by: "admin",
            ...(currentIsSSO && { is_sso: true, forecast_id: currentUser })
          });
        }
        break;

      case "receivemsg":
        if (data.chats?.[0] && data.chat_id === currentActive) {
          setMessages(prev => [...prev, data.chats[0]]);
        }
        break;

      default:
        console.warn("Unknown WS mode:", data.mode);
    }
  }, [sendJsonMessage]);

  const handleUserChatClick = (room: Rooms) => {
    if (room.chat_id === activeChatRef.current) return;

    const isSSOChat = !!(room.is_sso && room.forecast_id);
    const targetUserId = isSSOChat && room.forecast_id ? room.forecast_id : room.user_id.toString();

    activeChatRef.current = room.chat_id;
    currentUserIdRef.current = targetUserId;
    isSSORef.current = isSSOChat;

    setActiveChat(room.chat_id);
    setCurrentUserId(targetUserId);
    setIsSSO(isSSOChat);
    setUserData({ name: room.user_name || "", operation: room.operation || "" });
    setMessages([]);

    setRooms(prev => prev.map(r =>
      r.chat_id === room.chat_id ? { ...r, unread_admin: "0" } : r
    ));

    sendJsonMessage({
      mode: "latest",
      user_id: targetUserId,
      chat_id: room.chat_id,
      user_type: "admin",
      send_by: "admin",
      ...(isSSOChat && { is_sso: true, forecast_id: targetUserId })
    });

    if (isMobile) setShowSidebar(false);
  };

  const sendData = async (e: React.FormEvent) => {
    e.preventDefault();
    const currentActive = activeChatRef.current;
    const currentUser = currentUserIdRef.current;
    const currentIsSSO = isSSORef.current;

    if (!currentUser || !currentActive) {
      alert("No chat selected.");
      return;
    }
    if (text.trim() === "" && selectedFiles.length === 0) {
      alert("Message cannot be empty.");
      return;
    }

    if (selectedFiles.length > 0) {
      const formData = new FormData();
      formData.append("id", currentActive);
      formData.append("message", text);
      formData.append("user", "admin");
      formData.append("user_type", "admin");
      if (currentIsSSO) {
        formData.append("is_sso", "true");
        formData.append("forecast_id", currentUser);
      }
      selectedFiles.forEach((f) => formData.append("file", f, f.name));

      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_IP}/api/sendmessage/`,
          formData,
          { headers: { Authorization: "Basic YWRtaW46YWRtaW4=" } }
        );
      } catch (err: any) {
        if (err?.response?.status !== 500) {
          alert("Failed to send file. Please try again.");
        }
      }

      setText("");
      setSelectedFiles([]);
      setShowFileModal(false);

      sendJsonMessage({
        mode: "latest",
        user_id: currentUser,
        chat_id: currentActive,
        user_type: "admin",
        send_by: "admin",
        ...(currentIsSSO && { is_sso: true, forecast_id: currentUser })
      });
    } else {
      const payload: any = {
        mode: "sendmessage",
        chat_id: currentActive,
        message: text,
        user_type: "admin",
        user: localStorage.getItem("user_name"),
        user_id: localStorage.getItem("user_id"),
        send_by: "admin",
      };
      if (currentIsSSO) {
        payload.is_sso = true;
        payload.forecast_id = currentUser;
      }
      sendJsonMessage(payload);
      setText("");
    }
  };

  const saveFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFiles(Array.from(event.target.files));
      setShowFileModal(true);
      event.target.value = "";
    }
  };

  const handleModalClose = () => {
    setShowFileModal(false);
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const toggleUserExpand = (key: string) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  };

  const funcToogle = () => setShowSidebar((p) => !p);

  const renderMessages = () => (
    <>
      {messages.length > 0 ? (
        messages.map((msg, idx) => {
          const fileUrl = msg.file?.trim() || "";
          const hasFile = fileUrl !== "";
          const isImage = hasFile && (
            fileUrl.startsWith("data:image/") ||
            /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(fileUrl)
          );
          const resolvedSrc = fileUrl.startsWith("data:")
            ? fileUrl
            : `${process.env.REACT_APP_BACKEND_IP}/uploads/${fileUrl}`;

          const isOut = msg.user_type === "admin";

          return (
            <div key={idx} className={`tg-msg-row tg-msg-row--${isOut ? "out" : "in"}`}>
              {!isOut && (
                <div className="tg-msg-avatar-slot">
                  <div className={getAvatarClass(msg.user || userData.name, "sm")}>
                    {(msg.user || userData.name || "U").slice(0, 1)}
                  </div>
                </div>
              )}
              <div className={`tg-bubble tg-bubble--${isOut ? "out" : "in"}`}>
                {hasFile && (
                  isImage ? (
                    // eslint-disable-next-line jsx-a11y/img-redundant-alt
                    <img
                      src={resolvedSrc}
                      alt="Attached image"
                      className="tg-bubble__img"
                      onClick={() => {
                        setModalIsOpen(true);
                        setImagePreview(resolvedSrc);
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <a target="_blank" rel="noopener noreferrer" className="tg-bubble__file" href={resolvedSrc}>
                      <div className="tg-file-icon">
                        <DescriptionOutlinedIcon fontSize="small" />
                      </div>
                      <div className="tg-file-info">
                        <span className="tg-file-name">{msg.file_name || fileUrl}</span>
                        <span className="tg-file-size">Document</span>
                      </div>
                    </a>
                  )
                )}
                {msg.message && msg.message.trim() !== "" && (
                  <span className="tg-bubble__text">{msg.message}</span>
                )}
                <div className="tg-bubble__footer">
                  <span className="tg-bubble__time">{formatMessageTime(msg.date_time)}</span>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="tg-empty">
          <img src={Logo} className="tg-empty__logo" alt="Select a chat" />
          <span className="tg-empty__text">Select a conversation to start messaging</span>
        </div>
      )}
      <div ref={msgRef}>&nbsp;</div>
    </>
  );

  const renderInputBar = () => (
    <form onSubmit={sendData} className="tg-input-bar">
      <input
        type="file"
        accept="*"
        multiple
        onChange={saveFile}
        style={{ display: "none" }}
        ref={fileInputRef}
      />
      <div className="tg-input-attach" onClick={() => fileInputRef.current?.click()}>
        <AttachFileIcon fontSize="small" />
      </div>
      <input
        className="tg-input-field"
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Write a message..."
      />
      <button className="tg-send-btn" type="submit">
        <SendIcon fontSize="small" />
      </button>
    </form>
  );

  const renderHeader = () => (
    userData.name ? (
      <div className="tg-chat-header">
        {isMobile && (
          <div className="tg-chat-header__back" onClick={funcToogle}>
            <ArrowBackIcon fontSize="small" />
          </div>
        )}
        <div className={getAvatarClass(userData.name)}>
          {userData.name.slice(0, 1)}
        </div>
        <div className="tg-chat-header__info">
          <div className="tg-chat-header__name">{userData.name}</div>
          {userData.operation && (
            <div className="tg-chat-header__status">{userData.operation}</div>
          )}
        </div>
      </div>
    ) : null
  );

  const renderRooms = (fullWidth = false) => (
    <div className={`tg-sidebar${fullWidth ? " tg-sidebar--full" : ""}`}>
      <div className="tg-sidebar-topbar">
        <span className="tg-sidebar-title">Messages</span>
        {reduxData?.unread_user > 0 && (
          <div className="tg-badge tg-badge--total">{reduxData.unread_user}</div>
        )}
      </div>
      <div className="tg-search-wrap">
        <div className="tg-search">
          <span className="tg-search__icon"><SearchIcon fontSize="small" /></span>
          <input
            className="tg-search__input"
            type="search"
            placeholder="Search"
            value={searchUsers}
            onChange={(e) => setSearchUsers(e.target.value)}
          />
        </div>
      </div>
      <div className="tg-rooms">
        {groupedUsers
          .filter(g => searchUsers === "" || g.user_name?.toLowerCase().includes(searchUsers.toLowerCase()))
          .map((group) => {
            const isExpanded = expandedUsers.has(group.groupKey);
            const hasMultiple = group.rooms.length > 1;

            return (
              <div key={group.groupKey}>
                {hasMultiple ? (
                  <>
                    <div onClick={() => toggleUserExpand(group.groupKey)} className="tg-room-item">
                      <div className={getAvatarClass(group.user_name)}>
                        {group.user_name?.slice(0, 1)}
                      </div>
                      <div className="tg-room-body">
                        <div className="tg-room-row">
                          <span className="tg-room-item__name">
                            {group.user_name}
                            {group.is_sso && <span className="tg-sso-tag">SSO</span>}
                          </span>
                          {group.totalUnread > 0 && <div className="tg-badge">{group.totalUnread}</div>}
                          {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </div>
                        <div className="tg-room-row">
                          <span className="tg-room-item__preview">{group.rooms.length} Forecasts</span>
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="tg-group-children">
                        {group.rooms.map((room) => (
                          <div
                            key={room.chat_id}
                            onClick={() => handleUserChatClick(room)}
                            className={`tg-group-child-item${room.chat_id === activeChat ? " tg-group-child-item--active" : ""}`}
                          >
                            <span className="tg-group-child-name">
                              {room.operation || room.project_location || room.chat_id}
                            </span>
                            {+room.unread_admin !== 0 && <div className="tg-badge">{room.unread_admin}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    onClick={() => handleUserChatClick(group.rooms[0])}
                    className={`tg-room-item${group.rooms[0].chat_id === activeChat ? " tg-room-item--active" : ""}`}
                  >
                    <div className={getAvatarClass(group.user_name)}>
                      {group.user_name?.slice(0, 1)}
                    </div>
                    <div className="tg-room-body">
                      <div className="tg-room-row">
                        <span className="tg-room-item__name">
                          {group.user_name}
                          {group.is_sso && <span className="tg-sso-tag">SSO</span>}
                        </span>
                        {+group.rooms[0].unread_admin !== 0 && <div className="tg-badge">{group.rooms[0].unread_admin}</div>}
                      </div>
                      <div className="tg-room-row">
                        <span className="tg-room-item__preview">
                          {group.rooms[0].project_location || group.rooms[0].operation || group.rooms[0].chat_id}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );

  return (
    <div
      style={showSidebar && !isMobile ? { marginLeft: 260, width: "calc(100% - 260px)" } : {}}
      className="main"
    >
      {reduxData?.unread_user > 0 && !showSidebar && (
        <div className="tg-unread-notif" onClick={funcToogle}>
          {reduxData.unread_user} unread
        </div>
      )}

      <BasicModal
        open={showFileModal}
        sendData={sendData}
        setText={setText}
        text={text}
        image={selectedFiles}
        handleClose={handleModalClose}
        handleOpen={() => setShowFileModal(true)}
      />

      {modalIsOpen && (
        <ImagePreviewModal imageSrc={imagePreview} onClose={() => setModalIsOpen(false)} />
      )}

      <div className="tg-page-wrap">
        <div className="tg-root">
          {!isMobile && renderRooms()}
          {isMobile && showSidebar && renderRooms(true)}

          {(!isMobile || !showSidebar) && (
            <div className="tg-chat-area">
              {renderHeader()}
              <div className="tg-messages">
                {renderMessages()}
              </div>
              {activeChat && currentUserId && renderInputBar()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatAdmin;